import { base44 } from "@/api/base44Client";

/**
 * Check if a product is available on a specific date.
 * Only blocks when reservation_status = "Confirmed" AND payment_status = "Reservation Fee Paid".
 * @param {string} productId
 * @param {string} eventDate - YYYY-MM-DD
 * @param {string} excludeReservationId - optional, exclude when editing
 * @returns {Promise<{available: boolean, conflictingReservations: array}>}
 */
export async function checkProductAvailability(productId, eventDate, excludeReservationId = null) {
  const [reservations, product] = await Promise.all([
    base44.entities.Reservation.filter({
      product_id: productId,
      event_date: eventDate,
      reservation_status: "Confirmed",
      payment_status: "Reservation Fee Paid",
    }),
    base44.entities.Product.filter({ id: productId }),
  ]);

  const stock = product?.[0]?.stock ?? 1;

  const conflicting = reservations.filter(
    (r) => !excludeReservationId || r.id !== excludeReservationId
  );

  return {
    available: conflicting.length < stock,
    conflictingReservations: conflicting,
    stock,
  };
}

/**
 * Get unavailable dates for a product within a date range.
 * Only "Confirmed" + "Reservation Fee Paid" reservations count.
 */
export async function getProductAvailabilityRange(productId, startDate, endDate) {
  const [allReservations, product] = await Promise.all([
    base44.entities.Reservation.filter({
      product_id: productId,
      reservation_status: "Confirmed",
      payment_status: "Reservation Fee Paid",
    }),
    base44.entities.Product.filter({ id: productId }),
  ]);

  const stock = product?.[0]?.stock ?? 1;

  // Count bookings per date
  const bookingCounts = {};
  allReservations.forEach((r) => {
    bookingCounts[r.event_date] = (bookingCounts[r.event_date] || 0) + 1;
  });

  const availableDates = [];
  const unavailableDates = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const dateStr = currentDate.toISOString().split("T")[0];
    if ((bookingCounts[dateStr] || 0) >= stock) {
      unavailableDates.push(dateStr);
    } else {
      availableDates.push(dateStr);
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return { availableDates, unavailableDates };
}